import {
  type AgentSpanValueRef,
  ComputedCallStatuses,
} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/traceServerClientTypes';

import {
  type AggregationFunctionOption,
  type ComparisonOperatorOption,
  type MetricPathOption,
  type TimeUnitOption,
  type WeaveMetricThresholdConfig,
} from './types';

export const comparisonOperatorOptions: ComparisonOperatorOption[] = [
  {value: 'GREATER_THAN', label: 'Greater than'},
  {value: 'LESS_THAN', label: 'Less than'},
  {value: 'EQUAL', label: 'Equals'},
];

export const timeUnitOptions: TimeUnitOption[] = [
  {value: 'SECONDS', label: 'seconds'},
  {value: 'MINUTES', label: 'minutes'},
  {value: 'HOURS', label: 'hours'},
];

export const STATUS_VALUE_MAPPING = {
  [ComputedCallStatuses.running]: 0,
  [ComputedCallStatuses.success]: 1,
  [ComputedCallStatuses.error]: 2,
  [ComputedCallStatuses.descendant_error]: 3,
} as const;

export const REVERSE_STATUS_MAPPING = Object.fromEntries(
  Object.entries(STATUS_VALUE_MAPPING).map(([key, value]) => [value, key])
) as Record<number, keyof typeof ComputedCallStatuses>;

export const WEAVE_STATUS_PATH = 'summary.weave.status';
export const WEAVE_LATENCY_PATH = 'summary.weave.latency_ms';

/** Tailwind red-500 — used for error borders in react-select overrides */
export const ERROR_BORDER_COLOR = '#ef4444';

export const DEFAULT_WINDOW_SIZE = 10;
export const DEFAULT_SPAN_WINDOW_DURATION_SECONDS = 300;
export const MAX_WINDOW_SIZE = 100;
export const MAX_TIME_WINDOW_SECONDS = 24 * 60 * 60;

export const aggregationFunctionOptions: AggregationFunctionOption[] = [
  {value: 'mean', label: 'Mean'},
  {value: 'median', label: 'Median'},
  {value: 'min', label: 'Min'},
  {value: 'max', label: 'Max'},
  {value: 'mode', label: 'Mode'},
];

export const statusAggregationOptions: AggregationFunctionOption[] = [
  {value: 'percentage', label: 'Percentage of calls'},
  {value: 'count', label: 'Number of calls'},
];

// Spans-source (agent OTel) numeric metrics the backend can aggregate. Custom
// attributes are entered free-form as `custom_attrs_{float,int}.<key>`.
export const SPAN_TOTAL_TOKENS_PATH = 'total_tokens';
export const SPAN_DURATION_PATH = 'duration_ms';

// Token-subtype span columns, resolved as `source: 'field'` value refs. Mirrors
// `_NUMERIC_FIELD_METRICS` in alert_worker/spans_metric_source.py; keep in sync.
export const SPAN_INPUT_TOKENS_PATH = 'input_tokens';
export const SPAN_OUTPUT_TOKENS_PATH = 'output_tokens';
export const SPAN_REASONING_TOKENS_PATH = 'reasoning_tokens';
export const SPAN_CACHE_READ_INPUT_TOKENS_PATH = 'cache_read_input_tokens';
export const SPAN_CACHE_CREATION_INPUT_TOKENS_PATH =
  'cache_creation_input_tokens';
export const SPAN_FIELD_METRIC_PATHS: readonly string[] = [
  SPAN_INPUT_TOKENS_PATH,
  SPAN_OUTPUT_TOKENS_PATH,
  SPAN_REASONING_TOKENS_PATH,
  SPAN_CACHE_READ_INPUT_TOKENS_PATH,
  SPAN_CACHE_CREATION_INPUT_TOKENS_PATH,
];

// Cost metrics, resolved as `source: 'derived'` value refs computed server-side
// by the agent-spans-stats endpoint (price-joined; no span column backs them).
export const SPAN_TOTAL_COST_PATH = 'total_cost_usd';
export const SPAN_INPUT_COST_PATH = 'input_cost_usd';
export const SPAN_OUTPUT_COST_PATH = 'output_cost_usd';

// Error-rate metric path. Percentage aggregation -> error rate, count -> error
// count. Mirrors `_ERROR_METRIC_PATHS` in alert_worker/spans_metric_source.py;
// keep the two in sync.
export const SPAN_ERROR_RATE_PATH = 'status_code';
export const SPAN_ERROR_METRIC_PATHS: readonly string[] = [
  SPAN_ERROR_RATE_PATH,
  'is_error',
];

// Derived boolean the error-rate stats query aggregates (count_true/count),
// matching the backend is_error derivation (status_code = 'ERROR').
export const SPAN_IS_ERROR_VALUE_REF: AgentSpanValueRef = {
  source: 'derived',
  key: 'is_error',
};

export const spanMetricOptions: MetricPathOption[] = [
  {value: SPAN_ERROR_RATE_PATH, label: 'Error rate'},
  {value: SPAN_TOTAL_TOKENS_PATH, label: 'Total tokens'},
  {value: SPAN_INPUT_TOKENS_PATH, label: 'Input tokens'},
  {value: SPAN_OUTPUT_TOKENS_PATH, label: 'Output tokens'},
  {value: SPAN_REASONING_TOKENS_PATH, label: 'Reasoning tokens'},
  {value: SPAN_CACHE_READ_INPUT_TOKENS_PATH, label: 'Cache read input tokens'},
  {
    value: SPAN_CACHE_CREATION_INPUT_TOKENS_PATH,
    label: 'Cache creation input tokens',
  },
  {value: SPAN_DURATION_PATH, label: 'Latency (ms)'},
  {value: SPAN_TOTAL_COST_PATH, label: 'Total cost (USD)'},
  {value: SPAN_INPUT_COST_PATH, label: 'Input cost (USD)'},
  {value: SPAN_OUTPUT_COST_PATH, label: 'Output cost (USD)'},
];

// Spans stats support mean/min/max/sum only (no median/mode).
export const spanAggregationOptions: AggregationFunctionOption[] = [
  {value: 'mean', label: 'Mean'},
  {value: 'min', label: 'Min'},
  {value: 'max', label: 'Max'},
  {value: 'sum', label: 'Sum'},
];

// Error-rate aggregation modes, mirroring statusAggregationOptions for calls.
export const spanStatusAggregationOptions: AggregationFunctionOption[] = [
  {value: 'percentage', label: 'Percentage of error spans'},
  {value: 'count', label: 'Number of error spans'},
];

// metric_path -> agent_spans_stats value ref. Mirrors the alert worker's
// resolution in alert_worker/spans_metric_source.py; keep the two in sync.
export const SPAN_DERIVED_METRIC_ALIASES: Record<string, string> = {
  latency_ms: SPAN_DURATION_PATH,
};

export const SPAN_CUSTOM_ATTR_SOURCES: ReadonlyArray<{
  prefix: string;
  source: AgentSpanValueRef['source'];
}> = [
  {prefix: 'custom_attrs_float.', source: 'custom_attrs_float'},
  {prefix: 'custom_attrs_int.', source: 'custom_attrs_int'},
];

export const initialWeaveMetricConfig: WeaveMetricThresholdConfig = {
  metricFilter: {
    opVersionRefs: [],
    sourceType: 'OPERATION',
    alertCondition: 'THRESHOLD',
    comparisonOperator: 'GREATER_THAN',
    windowSize: DEFAULT_WINDOW_SIZE,
    aggregationFunction: 'mean',
  },
  allowAutoFocus: true,
};
